import { Button, Flex, Skeleton, Text, Tooltip, useMantineTheme } from '@mantine/core';
import {
	DATE_FORMATS,
	formatStringDateToRelative,
	formatStringDateToUI,
} from '@dealstack/shared/lib/format/dates/date';
import { InvestorInvitationStatus, SessionInfo } from '@dealstack/shared/lib/generated';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { StatusDotIcon } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/StatusDotIcon/StatusDotIcon';
import { SendInvitationModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendInvitationModal/SendInvitationModal.tsx';
import { useInviteInvestorModal } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/hooks/useInviteInvestorModal.ts';
import {
	ACCOUNT_ACTIVITY_SKELETON_HEIGHT,
	AccountActivityPanel,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/AccountActivityCard/AccountActivityCard.styles.tsx';
import { resolveAccountActivityState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/AccountActivityCard/accountActivity.utils.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { SessionDetails } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/SessionDetails/SessionDetails.tsx';

interface Props {
	investorId: string;
	invitationStatus?: InvestorInvitationStatus;
	latestSession?: SessionInfo | null;
	isLoading: boolean;
	onInvited: () => void;
}

export const AccountActivityCard = ({
	investorId,
	invitationStatus,
	latestSession,
	isLoading,
	onInvited,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const state = resolveAccountActivityState(invitationStatus);
	const {
		modalOpened,
		openModal,
		closeModal,
		investors,
		sendInvitations,
		isLoading: isInviting,
		isDisabled,
	} = useInviteInvestorModal({ investorId, onClose: onInvited });
	const theme = useMantineTheme();

	const inviteCopy = {
		'not-invited': {
			message: tMipManage('investors.details.account_activity.not_invited'),
			action: tMipManage('investors.details.account_activity.invite'),
		},
		'awaiting-login': {
			message: tMipManage('investors.details.account_activity.awaiting_login'),
			action: tMipManage('investors.details.account_activity.resend'),
		},
	};

	const renderInfoBox = () => {
		if (state === 'active' && latestSession) {
			return;
		}

		if (state === 'active') {
			return (
				<div
					className="info-box"
					data-testid={
						TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_ACCOUNT_ACTIVITY_MESSAGE
					}
				>
					<Text fz={14} c="black.0">
						{tMipManage('investors.details.account_activity.no_session')}
					</Text>
				</div>
			);
		}

		const copy = inviteCopy[state];

		return (
			<div className="info-box">
				<Flex direction="column" justify="center" align="center" gap="0.75rem" maw={200}>
					<Text
						fz={12}
						c="black.0"
						data-testid={
							TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_ACCOUNT_ACTIVITY_MESSAGE
						}
					>
						{copy.message}
					</Text>

					<Button
						data-testid={
							TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_ACCOUNT_ACTIVITY_ACTION
						}
						fw={600}
						variant="default"
						disabled={isDisabled}
						onClick={openModal}
					>
						{copy.action}
					</Button>
				</Flex>
			</div>
		);
	};

	const lastActiveAt = latestSession?.createdAt;
	const relativeLastActive = lastActiveAt ? formatStringDateToRelative(lastActiveAt) : undefined;
	const lastActiveLabel = tMipManage('investors.details.account_activity.last_active', {
		value: relativeLastActive ?? tMipManage('investors.details.account_activity.never'),
	});

	if (isLoading) {
		return <Skeleton height={ACCOUNT_ACTIVITY_SKELETON_HEIGHT} radius="0.5rem" />;
	}

	return (
		<>
			<AccountActivityPanel
				data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_ACCOUNT_ACTIVITY_CARD}
			>
				{renderInfoBox()}

				<div className="session">
					<Flex align="center" gap="0.25rem" className="session-status">
						<StatusDotIcon
							color={latestSession ? theme.colors.teal[8] : theme.colors.gray[9]}
						/>

						<Tooltip
							label={formatStringDateToUI(lastActiveAt, DATE_FORMATS.DATE_TIME_ALT)}
							disabled={!lastActiveAt}
						>
							<Text
								fz={12}
								c="dark.8"
								data-testid={
									TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_ACCOUNT_ACTIVITY_LAST_ACTIVE
								}
							>
								{lastActiveLabel}
							</Text>
						</Tooltip>
					</Flex>

					{latestSession && (
						<div
							data-testid={
								TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_ACCOUNT_ACTIVITY_SESSION
							}
						>
							<SessionDetails
								fullName={latestSession.fullName}
								email={latestSession.email}
								startDateAndTime={latestSession.createdAt}
								ipAddress={latestSession.ipAddress}
								location={latestSession.location}
								deviceInfo={latestSession.deviceInfo}
								isLoading={false}
								mode="simple"
							/>
						</div>
					)}
				</div>
			</AccountActivityPanel>

			{modalOpened && (
				<SendInvitationModal
					key={investors.length}
					onClose={closeModal}
					sendInvitation={sendInvitations}
					isLoading={isInviting}
					investors={investors}
				/>
			)}
		</>
	);
};
